import { createApp } from 'vue'
import { isDesktop } from '~/js/templates/index/helpers'
import { isIndexPage } from '~/js/helpers/page-helper'
import { isApp } from '~/js/helpers/user-agent-helper'
import FButton from '~/js/components/button'
import FModal from '~/js/components/modal'
import HeaderDesktop from '~/js/components/header/headerDesktop'
import HeaderMobile from '~/js/components/header/headerMobile'
import HeaderMobileFixed from '~/js/components/header/headerMobileFixed'
import IconButton from '~/js/components/header/iconButton'
import MenuDesktop from '~/js/components/header/menuDesktop'
import MenuMobile from '~/js/components/header/menuMobile'
import SearchWindowDesktop from '~/js/components/header/searchWindowDesktop'
import SearchWindowMobile from '~/js/components/header/searchWindowMobile'
import { RECENT_SEARCHES } from '~/js/constants/local-storage-keys.js'
import { LinkItem } from '~/js/models/linkItem'
import { getMenuItems } from './menuItemFactory'
import axios from 'axios'
import { baseUrlFrontApi } from '~/js/templates/index/helpers'
import { getQueryParams } from '~/js/helpers/url-helper'
import { getFSSearchedProducts } from '~/js/helpers/getFSProducts'

const ID = 'js-snippetsHeader'
const ID_MAIN = 'js-main'

document.addEventListener('DOMContentLoaded', async () => {
  const element = document.getElementById(ID)
  if (!element) return

  const { headerDebounceDesktop, headerDebounceMobile } = element.dataset
  const headerDebounce = isDesktop()
    ? headerDebounceDesktop
    : headerDebounceMobile

  const defaultCollections = (element.dataset.defaultCollections || '')
    .split(';')
    .filter((c) => !!c)
    .map((c) => {
      const collectionData = c.split(',')
      return {
        id: collectionData[0],
        title: collectionData[1],
        handle: collectionData[2],
      }
    })

  const routes = {}
  Object.keys(element.dataset)
    .filter((key) => {
      return key.startsWith('url')
    })
    .forEach((key) => {
      const keyPascal = key.replace('url', '')
      const keyCamel = `${keyPascal[0].toLowerCase()}${keyPascal.slice(1)}`
      routes[keyCamel] = element.dataset[key]
    })
  const localStorageRecentSearches = localStorage.getItem(RECENT_SEARCHES)
  const recentSearchesAll = localStorageRecentSearches
    ? localStorageRecentSearches.split(',').map((item) => {
        const url = `/search?sort_by=mf-additional-published_at_time-descending&q=${item}`
        return new LinkItem({ title: item }, url)
      })
    : []

  const noImageUrl = element.dataset.noImageUrl
  const menuItems = getMenuItems(element)

  let debounceTimerId = undefined

  createApp({
    name: 'SnippetsHeader',
    components: {
      FButton,
      FModal,
      HeaderDesktop,
      HeaderMobile,
      HeaderMobileFixed,
      IconButton,
      MenuDesktop,
      MenuMobile,
      SearchWindowDesktop,
      SearchWindowMobile,
    },
    data() {
      return {
        articleItems: [],
        collectionItems: [],
        isDesktop: isDesktop(),
        isMenuOpen: false,
        isSearchWindowOpen: false,
        menuItems,
        products: [],
        querySuggestions: [],
        recentSearchesAll,
        renderH1: isIndexPage,
        routes,
        searchKeyword: '',
        threshold: 0,
        headerSize: {
          height: 0,
          width: 0,
        },
      }
    },
    created() {
      window.addEventListener('click', (e) => {
        const x = e.pageX - window.scrollX
        const y = e.pageY - window.scrollY
        const element = document
          .elementFromPoint(x, y)
          .closest('.js-ignore-click-event')
        if (element) return

        if (this.isDesktop) {
          if (this.isMenuOpen) {
            e.preventDefault()
            this.isMenuOpen = false
          }
          if (this.isSearchWindowOpen) {
            e.preventDefault()
            this.closeSearchWindow()
          }
        }
      })
    },
    computed: {
      hasNativeHeader() {
        return isApp()
      },
      recentSearches() {
        return (
          this.searchKeyword
            ? this.recentSearchesAll.filter((item) => {
                return item.title.includes(this.searchKeyword)
              })
            : this.recentSearchesAll
        ).slice(0, 5)
      },
      positionMenu() {
        const right = (window.innerWidth - this.headerSize.width) / 2
        const height = this.headerSize.height
        return {
          top: `${height}px`,
          right: `${right}px`,
          maxHeight: `calc(100% - ${height}px)`,
        }
      },
      positionSearchWindow() {
        const height = window.innerHeight - this.headerSize.height - 10
        return {
          top: `${this.headerSize.height}px`,
          height: `${height}px`,
        }
      },
    },
    mounted() {
      window.zvRenderReviewSummary(element)
    },
    methods: {
      closeSearchWindow() {
        this.isSearchWindowOpen = false
        document.getElementsByTagName('html')[0].style.overflow = null
        document.body.style.overflow = null
      },
      deleteRecentSearch(target) {
        const index = this.recentSearchesAll.findIndex((item) => {
          return item.title === target.title
        })
        if (index < 0) return
        this.recentSearchesAll.splice(index, 1)
        localStorage.setItem(
          RECENT_SEARCHES,
          this.recentSearchesAll.map((item) => item.title).join(',')
        )
      },
      onSearchInputEntered(searchKeyword) {
        const localStorageRecentSearches = localStorage.getItem(RECENT_SEARCHES)
        const recentSearches = localStorageRecentSearches
          ? localStorageRecentSearches.split(',')
          : []
        const index = recentSearches.indexOf(searchKeyword)
        if (index > -1) recentSearches.splice(index, 1)
        recentSearches.unshift(searchKeyword)

        localStorage.setItem(RECENT_SEARCHES, recentSearches.join(','))

        window.location.replace(
          `/search?sort_by=mf-additional-published_at_time-descending&q=${encodeURIComponent(
            searchKeyword
          )}`
        )
      },
      onSearchKeywordInput(searchKeyword) {
        this.searchKeyword = searchKeyword
        if (debounceTimerId) clearTimeout(debounceTimerId)
        debounceTimerId = setTimeout(this.search, headerDebounce)
      },
      onThresholdChanged(value) {
        this.threshold = value
      },
      openMenu() {
        this.isMenuOpen = true
      },
      openSearchWindow() {
        if (this.isSearchWindowOpen) return

        this.isSearchWindowOpen = true

        this.fetchSearchResults(true)

        this.isMenuOpen = false
        document.getElementsByTagName('html')[0].style.overflow = 'hidden'
        document.body.style.overflow = 'hidden'
        this.$nextTick(() => {
          const searchInput = document.querySelector(
            '.c-header-search-input__input'
          )
          if (searchInput) {
            searchInput.focus()
          }
        })
      },
      async fetchSearchResults() {
        try {
          const [{ products, suggestions, collections }, { data: articles }] =
            await Promise.all([
              await getFSSearchedProducts({
                searchKeyword: this.searchKeyword,
                callback: 'header-v3',
              }),
              await axios.get(
                `${baseUrlFrontApi}/articles/search?${getQueryParams(
                  {
                    keyword: this.searchKeyword,
                    limit: 6,
                    blog_handles: 'coordinate,feature',
                  },
                  true
                )}`
              ),
            ])

          this.products = products
          this.querySuggestions = (suggestions || []).map(
            (s) =>
              new LinkItem(
                { title: s.keyword },
                `/search?sort_by=mf-additional-published_at_time-descending&q=${s.keyword}`
              )
          )
          this.collectionItems = (
            this.searchKeyword ? collections || [] : defaultCollections
          ).map(
            (c) =>
              new LinkItem(
                { id: c.id, title: c.title },
                `/collections/${c.handle}`
              )
          )
          this.articleItems = articles.map(
            (article) =>
              new LinkItem(
                { ...article, image: article.image.url || noImageUrl },
                `/blogs/${article.blog.handle}/${article.handle}`
              )
          )
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error)
        }
      },
      search() {
        this.fetchSearchResults()
      },
      setHeaderSize(headerSize) {
        this.headerSize = headerSize

        // メインコンテンツにヘッダー高さ分の余白を付与
        if (isDesktop()) {
          const elementMain = document.getElementById(ID_MAIN)
          if (elementMain)
            elementMain.style.marginTop = `${headerSize.height}px`
        }

        element.dataset.height = headerSize.height
      },
    },
  }).mount(element)
})
